import React from 'react'
import ReactDOM from 'react-dom/client'

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    console.error('Hub render error:', error, info?.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            fontFamily: 'system-ui, sans-serif',
            padding: '24px',
            maxWidth: '520px',
            margin: '40px auto',
          }}
        >
          <h1 style={{ fontSize: '1.1rem' }}>Something went wrong loading the hub</h1>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f4f4f5',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '13px',
            }}
          >
            {String(this.state.error)}
          </pre>
          <p style={{ fontSize: '14px', color: '#52525b' }}>
            Try a hard refresh (Ctrl+Shift+R) or another browser. If this persists, check the
            browser console for details.
          </p>
        </div>
      )
    }
    return this.props.children
  }
}

function renderLoadFailure(rootEl, err) {
  console.error('Hub module failed to load:', err)
  ReactDOM.createRoot(rootEl).render(
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        padding: '24px',
        maxWidth: '520px',
        margin: '40px auto',
      }}
    >
      <h1 style={{ fontSize: '1.1rem' }}>Could not load the hub</h1>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: '#f4f4f5',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '13px',
        }}
      >
        {String(err)}
      </pre>
      <p style={{ fontSize: '14px', color: '#52525b' }}>
        If you see a network or MIME error, try another port (stop other dev servers), hard refresh,
        or run from the project root after <code>npm install</code>.
      </p>
    </div>
  )
}

declare const __BUILD_TIME__: string
declare const __GIT_SHA__: string

const BUILD_INFO = {
  sha: typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : 'dev',
  built: typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : new Date().toISOString(),
}
;(globalThis as any).__PUZZLES_BUILD__ = BUILD_INFO
console.info(`[Puzzles] ${BUILD_INFO.sha} (${BUILD_INFO.built})`)

async function boot() {
  const rootEl = document.getElementById('root')
  if (!rootEl) {
    console.error('[Puzzles] Missing #root — app cannot mount (check index.html).')
    return
  }

  let App
  try {
    ;({ default: App } = await import('./App.tsx'))
  } catch (err) {
    renderLoadFailure(rootEl, err)
    return
  }

  ReactDOM.createRoot(rootEl).render(
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  )
}

boot()
